import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { quotesService, Quote, CreateQuoteRequest, UpdateQuoteRequest } from '../services/quotesService';

// Query keys for quotes
export const quotesQueryKeys = {
  all: ['quotes'] as const,
  lists: () => [...quotesQueryKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...quotesQueryKeys.lists(), { filters }] as const,
  details: () => [...quotesQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...quotesQueryKeys.details(), id] as const,
  stats: () => [...quotesQueryKeys.all, 'stats'] as const,
};

// Main quotes data hook
export const useQuotesData = () => {
  return useQuery({
    queryKey: quotesQueryKeys.lists(),
    queryFn: async () => {
      console.log('🔄 Fetching quotes from API...');

      const quotes = await quotesService.getQuotes();
      console.log('✅ Quotes fetched from API:', quotes.length, 'quotes');
      return quotes;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes in cache
    refetchOnWindowFocus: false,
    retry: 2,
    // Don't cache errors - refetch on mount if previous attempt failed
    refetchOnMount: 'always',
  });
};

// Single quote data hook
export const useQuoteData = (id: string) => {
  return useQuery({
    queryKey: quotesQueryKeys.detail(id),
    queryFn: () => quotesService.getQuote(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Quote statistics hook
export const useQuoteStats = () => {
  return useQuery({
    queryKey: quotesQueryKeys.stats(),
    queryFn: () => quotesService.getQuoteStats(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Mutation hooks for quote operations
export const useCreateQuote = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (quoteData: CreateQuoteRequest) => quotesService.createQuote(quoteData),
    onSuccess: () => {
      // Invalidate and refetch quotes list
      queryClient.invalidateQueries({ queryKey: quotesQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: quotesQueryKeys.stats() });
    },
  });
};

export const useUpdateQuote = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateQuoteRequest }) => 
      quotesService.updateQuote(id, data),
    onSuccess: (updatedQuote) => {
      // Update the quotes list cache
      queryClient.setQueryData(quotesQueryKeys.lists(), (oldData: Quote[] | undefined) => {
        if (!oldData) return [updatedQuote];
        return oldData.map(quote => quote.id === updatedQuote.id ? updatedQuote : quote);
      });
      
      // Update the individual quote cache
      queryClient.setQueryData(quotesQueryKeys.detail(updatedQuote.id), updatedQuote);
      
      // Invalidate stats
      queryClient.invalidateQueries({ queryKey: quotesQueryKeys.stats() });
    },
  });
};

export const useUpdateQuoteStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, status, notes }: { id: string; status: Quote['status']; notes?: string }) => 
      quotesService.updateQuoteStatus(id, { status, notes }),
    onSuccess: (updatedQuote) => {
      // Update caches
      queryClient.setQueryData(quotesQueryKeys.lists(), (oldData: Quote[] | undefined) => {
        if (!oldData) return [updatedQuote];
        return oldData.map(quote => quote.id === updatedQuote.id ? updatedQuote : quote);
      });
      
      queryClient.setQueryData(quotesQueryKeys.detail(updatedQuote.id), updatedQuote);
      queryClient.invalidateQueries({ queryKey: quotesQueryKeys.stats() });
    },
  });
};

export const useDeleteQuote = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => quotesService.deleteQuote(id),
    onSuccess: (_, deletedId) => {
      // Remove from quotes list cache
      queryClient.setQueryData(quotesQueryKeys.lists(), (oldData: Quote[] | undefined) => {
        if (!oldData) return [];
        return oldData.filter(quote => quote.id !== deletedId);
      });
      
      // Remove individual quote cache
      queryClient.removeQueries({ queryKey: quotesQueryKeys.detail(deletedId) });
      
      // Invalidate stats
      queryClient.invalidateQueries({ queryKey: quotesQueryKeys.stats() });
    },
  });
};

export const useConvertQuoteToJob = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => quotesService.convertToJob(id),
    onSuccess: () => {
      // Backend returns { jobId, engineerId }; just refetch lists + jobs
      queryClient.invalidateQueries({ queryKey: quotesQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: quotesQueryKeys.stats() });
    },
  });
};

export const useSendQuote = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => quotesService.sendQuote(id),
    onSuccess: (updatedQuote) => {
      // Update caches with sent status
      queryClient.setQueryData(quotesQueryKeys.lists(), (oldData: Quote[] | undefined) => {
        if (!oldData) return [updatedQuote];
        return oldData.map(quote => quote.id === updatedQuote.id ? updatedQuote : quote);
      });
      
      queryClient.setQueryData(quotesQueryKeys.detail(updatedQuote.id), updatedQuote);
      queryClient.invalidateQueries({ queryKey: quotesQueryKeys.stats() });
    },
  });
};

// Utility hook for refreshing quotes data
export const useRefreshQuotes = () => {
  const queryClient = useQueryClient();
  
  return () => {
    queryClient.invalidateQueries({ queryKey: quotesQueryKeys.all });
  };
};
